(() => {
  if (customElements.get("ingredient-spotlight-cards")) return;

  class IngredientSpotlightCards extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.activeId = null;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
    }

    connectedCallback() {
      this.sectionId =
        this.closest(".shopify-section")?.dataset?.sectionId || null;
      this.hotspots = Array.from(
        this.querySelectorAll("[data-ingredient-card-hotspot]")
      );
      this.cards = Array.from(
        this.querySelectorAll("[data-ingredient-card]")
      );
      this.labels = Array.from(
        this.querySelectorAll("[data-ingredient-card-label]")
      );
      this.bindEvents();
      this.setDefaultActive();
      this.bindEditorEvents();
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(element, event, handler) {
      if (!element) return;
      element.addEventListener(event, handler);
      this.cleanup.push(() => element.removeEventListener(event, handler));
    }

    bindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;
      this.addListener(document, "shopify:block:select", this.onBlockSelect);
      this.addListener(window, "shopify:block:select", this.onBlockSelect);
      this.addListener(document, "shopify:section:select", this.onSectionSelect);
      this.addListener(window, "shopify:section:select", this.onSectionSelect);
    }

    onBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (blockId) this.setActive(blockId);
    }

    onSectionSelect(event) {
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId === this.sectionId) {
        this.setDefaultActive();
      }
    }

    bindEvents() {
      const activate = (event) => {
        const id =
          event.currentTarget.dataset.ingredientCardHotspot ||
          event.currentTarget.dataset.ingredientCard;
        if (id) this.setActive(id);
      };

      this.hotspots.forEach((hotspot) => {
        this.addListener(hotspot, "click", activate);
        this.addListener(hotspot, "mouseenter", activate);
      });

      this.cards.forEach((card) => {
        this.addListener(card, "click", activate);
        this.addListener(card, "mouseenter", activate);
      });
    }

    setDefaultActive() {
      const defaultCard =
        this.cards.find((card) => card.hasAttribute("data-default-active")) ||
        this.cards[0];
      const id = defaultCard?.dataset?.ingredientCard;
      if (id) this.setActive(id);
    }

    setActive(id) {
      if (!id) return;
      this.activeId = id;

      this.hotspots.forEach((hotspot) => {
        const isActive = hotspot.dataset.ingredientCardHotspot === id;
        hotspot.classList.toggle("is-active", isActive);
        hotspot.setAttribute("aria-pressed", isActive ? "true" : "false");
        const ring = hotspot.querySelector("[data-ingredient-card-ring]");
        const dot = hotspot.querySelector("[data-ingredient-card-dot]");
        if (ring) {
          ring.classList.toggle("opacity-100", isActive);
          ring.classList.toggle("opacity-0", !isActive);
          ring.style.animation = isActive
            ? "ingredient-card-ripple 2s cubic-bezier(0, 0, 0.2, 1) infinite"
            : "";
        }
        if (dot) {
          dot.classList.toggle("bg-foreground", isActive);
          dot.classList.toggle("border-foreground", isActive);
          dot.classList.toggle("scale-125", isActive);
          dot.classList.toggle("bg-background", !isActive);
          dot.classList.toggle("border-foreground/40", !isActive);
          dot.classList.toggle("scale-100", !isActive);
        }
      });

      this.labels.forEach((label) => {
        const isActive = label.dataset.ingredientCardLabel === id;
        label.classList.toggle("is-active", isActive);
        label.classList.toggle("opacity-100", isActive);
        label.classList.toggle("translate-x-0", isActive);
        label.classList.toggle("opacity-0", !isActive);
        label.classList.toggle("-translate-x-2", !isActive);
      });

      this.cards.forEach((card) => {
        const isActive = card.dataset.ingredientCard === id;
        card.classList.toggle("is-active", isActive);
        card.classList.toggle("border-foreground", isActive);
        card.classList.toggle("border-foreground/20", !isActive);
        const title = card.querySelector(".ingredient-spotlight-cards__card-title");
        if (title) {
          title.classList.toggle("opacity-100", isActive);
          title.classList.toggle("md:opacity-60", !isActive);
        }
        card.setAttribute("aria-hidden", "false");
      });
    }
  }

  customElements.define("ingredient-spotlight-cards", IngredientSpotlightCards);
})();
